<template>
  <div>
    <h3 class="text-h6 mb-4">Configuración de Estilos</h3>
    
    <v-alert type="info" text dense class="mb-4">
      Personalice los colores, fuentes y márgenes del remito.
    </v-alert>

    <!-- Logo -->
    <v-card class="mb-4">
      <v-card-title class="subtitle-1">
        <v-icon left>mdi-image</v-icon>
        Logo
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="estilosInternos.logo.url"
              label="URL del Logo"
              outlined
              dense
              prepend-icon="mdi-link"
              @input="emitirCambios"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="estilosInternos.logo.width"
              label="Ancho"
              outlined
              dense
              placeholder="60mm"
              @input="emitirCambios"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="estilosInternos.logo.height"
              label="Alto"
              outlined
              dense
              placeholder="20mm"
              @input="emitirCambios"
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-radio-group
              v-model="estilosInternos.logo.position"
              row
              label="Posición del Logo"
              @change="emitirCambios"
            >
              <v-radio label="Izquierda" value="left"></v-radio>
              <v-radio label="Centro" value="center"></v-radio>
              <v-radio label="Derecha" value="right"></v-radio>
            </v-radio-group>
          </v-col>
          <v-col cols="12">
            <v-radio-group
              v-model="estilosInternos.logo.layout"
              label="Layout del Encabezado"
              @change="emitirCambios"
            >
              <v-radio 
                label="Logo y datos lado a lado (horizontal)" 
                value="horizontal"
              ></v-radio>
              <v-radio 
                label="Logo arriba, datos abajo (vertical)" 
                value="vertical"
              ></v-radio>
            </v-radio-group>
            <v-alert type="info" dense text class="mt-2">
              <strong>Vertical:</strong> Logo en la esquina superior izquierda con los datos de la empresa debajo (estilo Muresco)
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Colores -->
    <v-card class="mb-4">
      <v-card-title class="subtitle-1">
        <v-icon left>mdi-palette</v-icon>
        Colores
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="estilosInternos.colores.headerBackground"
              label="Fondo de Encabezados"
              outlined
              dense
              @input="emitirCambios"
            >
              <template v-slot:append>
                <input
                  type="color"
                  :value="estilosInternos.colores.headerBackground"
                  @input="estilosInternos.colores.headerBackground = $event.target.value; emitirCambios()"
                  style="width: 40px; height: 40px; border: none; cursor: pointer;"
                />
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="estilosInternos.colores.headerText"
              label="Texto de Encabezados"
              outlined
              dense
              @input="emitirCambios"
            >
              <template v-slot:append>
                <input
                  type="color"
                  :value="estilosInternos.colores.headerText"
                  @input="estilosInternos.colores.headerText = $event.target.value; emitirCambios()"
                  style="width: 40px; height: 40px; border: none; cursor: pointer;"
                />
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="estilosInternos.colores.borderColor"
              label="Color de Bordes"
              outlined
              dense
              @input="emitirCambios"
            >
              <template v-slot:append>
                <input
                  type="color"
                  :value="estilosInternos.colores.borderColor"
                  @input="estilosInternos.colores.borderColor = $event.target.value; emitirCambios()"
                  style="width: 40px; height: 40px; border: none; cursor: pointer;"
                />
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="estilosInternos.colores.accentColor"
              label="Color de Acento"
              outlined
              dense
              @input="emitirCambios"
            >
              <template v-slot:append>
                <input
                  type="color"
                  :value="estilosInternos.colores.accentColor"
                  @input="estilosInternos.colores.accentColor = $event.target.value; emitirCambios()"
                  style="width: 40px; height: 40px; border: none; cursor: pointer;"
                />
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Fuentes -->
    <v-card class="mb-4">
      <v-card-title class="subtitle-1">
        <v-icon left>mdi-format-font</v-icon>
        Fuentes
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="estilosInternos.fuentes.familia"
              :items="fuentesDisponibles"
              label="Familia de Fuente"
              outlined
              dense
              @change="emitirCambios"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="estilosInternos.fuentes.tamanoBase"
              label="Tamaño Base"
              outlined
              dense
              placeholder="14px"
              @input="emitirCambios"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="estilosInternos.fuentes.tamanoTitulos"
              label="Tamaño Títulos"
              outlined
              dense
              placeholder="12px"
              @input="emitirCambios"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Márgenes -->
    <v-card class="mb-4">
      <v-card-title class="subtitle-1">
        <v-icon left>mdi-page-layout-body</v-icon>
        Márgenes
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="estilosInternos.margenes.top"
              label="Superior"
              outlined
              dense
              placeholder="12mm"
              @input="emitirCambios"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="estilosInternos.margenes.right"
              label="Derecho"
              outlined
              dense
              placeholder="8mm"
              @input="emitirCambios"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="estilosInternos.margenes.bottom"
              label="Inferior"
              outlined
              dense
              placeholder="6mm"
              @input="emitirCambios"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="estilosInternos.margenes.left"
              label="Izquierdo"
              outlined
              dense
              placeholder="8mm"
              @input="emitirCambios"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Pie de Página -->
    <v-card class="mb-4">
      <v-card-title class="subtitle-1">
        <v-icon left>mdi-page-layout-footer</v-icon>
        Pie de Página
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="estilosInternos.footer.leyenda.fontSize"
              label="Tamaño texto leyenda"
              outlined
              dense
              placeholder="9pt"
              @input="emitirCambios"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="estilosInternos.footer.leyenda.maxWidth"
              label="Ancho máx. leyenda"
              outlined
              dense
              placeholder="120mm"
              hint="Controla cuánto espacio ocupa la leyenda hacia la derecha"
              persistent-hint
              @input="emitirCambios"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center">
            <v-switch
              v-model="estilosInternos.footer.leyenda.bold"
              label="Leyenda en negrita"
              @change="emitirCambios"
            ></v-switch>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="estilosInternos.footer.datosFiscal.fontSize"
              label="Tamaño texto datos fiscales"
              outlined
              dense
              placeholder="9pt"
              @input="emitirCambios"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center">
            <v-switch
              v-model="estilosInternos.footer.datosFiscal.boldLabels"
              label="Labels (CAI, Vencimiento) en negrita"
              @change="emitirCambios"
            ></v-switch>
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center">
            <v-switch
              v-model="estilosInternos.footer.datosFiscal.boldValues"
              label="Valores en negrita"
              @change="emitirCambios"
            ></v-switch>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'EstilosEditor',
  
  props: {
    estilos: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      estilosInternos: {
        logo: {
          url: '',
          width: '60mm',
          height: '20mm',
          position: 'left',
          layout: 'vertical'
        },
        colores: {
          headerBackground: '#e0e0e0',
          headerText: '#000000',
          borderColor: '#000000',
          accentColor: '#0066cc'
        },
        fuentes: {
          familia: 'Arial, sans-serif',
          tamanoBase: '14px',
          tamanoTitulos: '12px'
        },
        margenes: {
          top: '12mm',
          right: '8mm',
          bottom: '6mm',
          left: '8mm'
        },
        footer: {
          leyenda: {
            fontSize: '9pt',
            maxWidth: '120mm',
            bold: false
          },
          datosFiscal: {
            fontSize: '9pt',
            boldLabels: true,
            boldValues: false
          }
        }
      },
      footerDefaults: {
        leyenda: {
          fontSize: '9pt',
          maxWidth: '120mm',
          bold: false
        },
        datosFiscal: {
          fontSize: '9pt',
          boldLabels: true,
          boldValues: false
        }
      },
      fuentesDisponibles: [
        'Arial, sans-serif',
        'Helvetica, sans-serif',
        'Times New Roman, serif',
        'Courier New, monospace',
        'Georgia, serif',
        'Verdana, sans-serif'
      ]
    }
  },

  watch: {
    estilos: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.estilosInternos = this.construirEstilos(JSON.parse(JSON.stringify(newVal)))
        }
      }
    }
  },

  methods: {
    construirEstilos(estilos) {
      if (!estilos.footer) estilos.footer = {}

      estilos.footer.leyenda = {
        fontSize: estilos.footer.leyenda?.fontSize || this.footerDefaults.leyenda.fontSize,
        maxWidth: estilos.footer.leyenda?.maxWidth || this.footerDefaults.leyenda.maxWidth,
        bold: estilos.footer.leyenda?.bold ?? this.footerDefaults.leyenda.bold
      }

      estilos.footer.datosFiscal = {
        fontSize: estilos.footer.datosFiscal?.fontSize || this.footerDefaults.datosFiscal.fontSize,
        boldLabels: estilos.footer.datosFiscal?.boldLabels ?? this.footerDefaults.datosFiscal.boldLabels,
        boldValues: estilos.footer.datosFiscal?.boldValues ?? this.footerDefaults.datosFiscal.boldValues
      }

      return estilos
    },
    emitirCambios() {
      this.$emit('update', this.estilosInternos)
    }
  }
}
</script>
